import React from "react";

interface CurrentPatient {
    covidInfection: Date;
    entryDate: string;
    identification: number;
    yearOfBirth: number;
    labValueDateBefore: Date | undefined;
    labValueDateAfter: Date | undefined;
    labValueDateOneMonth: Date | undefined;
    labValueDateThreeMonths: Date | undefined;
    scoreDateBefore: Date | undefined;
    scoreDateAfter: Date | undefined;
    scoreDateOneMonth: Date | undefined;
    scoreDateThreeMonths: Date | undefined;
}

interface AxiosPatient {
    antibodies: [{
        //ToDo: optional machen
        id: number,
        name: string,
        state: string,
        patientId: number
    }],
    otherAak: string,
    approvalBioSampling: boolean,
    averageTreatedPlasmaVolume: number | undefined,
    bioSampleAfterTreatment: string,
    bioSampleBeforeTreatment: string,
    center: {
        id: number,
        identification: string
    },
    complication: {
        cardial: boolean,
        id: number,
        neurological: boolean,
        otherComplication: boolean,
        otherOrganManifestation: boolean,
        patientId: number,
        pulmonary: boolean,
        tromboembolic: boolean
    },
    otherComplicationName: string,
    covidInfection: Date;
    entryDate: Date;
    height: number | undefined,
    weight: number | undefined,
    id: number,
    identification: number,
    labValues: [{
        id: number,
        measurementDate: Date | null;
        name: string,
        patientId: number,
        pointOfMeasurement: string,
        unit: string,
        value: number | undefined
    }],
    previousTherapy: {
        cardiology: boolean,
        hematology: boolean,
        id: number,
        neurology: boolean,
        otherTherapy: boolean,
        psychology: boolean,
        pneumology: boolean,
    },
    otherTherapyName: string,
    scores: [{
        bdi: number,
        borgCr: number,
        borgRpe: number,
        canadianScore: number,
        epworthSleepinessScale: number,
        eq5Score: number,
        fss: number,
        id: number,
        moca: number,
        patientId: number,
        pointOfMeasurement: string,
        measurementDate: Date | null,
        sixMinuteWalk: number
    }],
    severity: string,
    sex: string,
    genotype: string,
    symptoms:[{
        fatigue: boolean,
        fever: boolean,
        fluLikeSymptoms: boolean,
        generalizedPain: boolean,
        id: number,
        patientId: number,
        jointPain: boolean,
        muscularPain: boolean,
        pointOfMeasurement: string,
        weightLoss: boolean
    }],
    typeOfExtracorporealTherapy: string,
    vaccinated: boolean | undefined,
    recurrentDisease: boolean | undefined,
    weeks: [{
        id: number,
        number: number,
        numberOfTreatments: number
        patientId: number
    }],
    yearOfBirth: number | undefined
}

export const defaultAxiosPatient: AxiosPatient = ({
    antibodies: [{
        id: 0,
        name: "beta1aak",
        state: "negative",
        patientId: 0
    }],
    otherAak: "",
    approvalBioSampling: true,
    averageTreatedPlasmaVolume: undefined,
    bioSampleAfterTreatment: "",
    bioSampleBeforeTreatment: "",
    center: {
        id: 0,
        identification: ""
    },
    complication: {
        cardial: false,
        id: 0,
        neurological: false,
        otherComplication: false,
        otherOrganManifestation: false,
        patientId: 0,
        pulmonary: false,
        tromboembolic: false,
    },
    otherComplicationName: "",
    covidInfection: new Date(0),
    entryDate: new Date(0),
    height: undefined,
    weight: undefined,
    id: 0,
    identification: 66,
    labValues: [{
        id: 0,
        measurementDate: null,
        name: "",
        patientId: 0,
        pointOfMeasurement: "",
        unit: "",
        value: undefined
    }],
    previousTherapy: {
        cardiology: false,
        hematology: false,
        id: 0,
        neurology: false,
        otherTherapy: false,
        psychology: false,
        pneumology: false,
    },
    otherTherapyName: "",
    scores: [{
        bdi: 0,
        borgCr: 0,
        borgRpe: 0,
        canadianScore: 0,
        epworthSleepinessScale: 0,
        eq5Score: 0,
        fss: 0,
        id: 0,
        moca: 0,
        patientId: 0,
        pointOfMeasurement: "",
        measurementDate: null,
        sixMinuteWalk: 0
    }],
    severity: "",
    sex: "",
    genotype: "",
    symptoms:[{
        fatigue: false,
        fever: false,
        fluLikeSymptoms: false,
        generalizedPain: false,
        id: 0,
        patientId: 0,
        jointPain: false,
        muscularPain: false,
        pointOfMeasurement: "",
        weightLoss: false
    }],
    typeOfExtracorporealTherapy: "",
    vaccinated: undefined,
    recurrentDisease: undefined,
    weeks: [{
        id: 0,
        number: 0,
        numberOfTreatments: 0,
        patientId: 0
    }],
    yearOfBirth: undefined
});

export const defaultCurrentPatient: CurrentPatient = ({
    covidInfection: new Date(0),
    entryDate: "",
    identification: 0,
    yearOfBirth: 0,
    labValueDateBefore: undefined,
    labValueDateAfter: undefined,
    labValueDateOneMonth: undefined,
    labValueDateThreeMonths: undefined,
    scoreDateBefore: undefined,
    scoreDateAfter: undefined,
    scoreDateOneMonth: undefined,
    scoreDateThreeMonths: undefined
});

const GlobalContext = React.createContext({
    showLoginDialog: false,
    setShowLoginDialog: (showLogin: boolean) => {},
    showConsentDialog: false,
    setShowConsentDialog: (showLogin: boolean) => {},
    buttonLoginLogout: "Login",
    setButtonLoginLogout: (buttonLoginLogout: string) => {},
    username: "",
    setUsername: (username: string) => {},
    password: "",
    setPassword: (password: string) => {},
    oldPassword: "",
    setOldPassword: (newPassword: string) => {},
    newPassword1: "",
    setNewPassword1: (newPassword: string) => {},
    newPassword2: "",
    setNewPassword2: (newPassword: string) => {},
    currentUser: {},
    setCurrentUser: (user: object) => {},
    isAuthenticated: false,
    setIsAuthenticated: (isAuthenticated: boolean) => {},
    currentCenterId: 0,
    setCurrentCenterId: (id: number) => {},
    selectedPatientId: 0,
    setSelectedPatientId: (id: number) => {},
    selectedPatientIdentification: 0,
    setSelectedPatientIdentification: (id: number) => {},
    currentPatient: defaultCurrentPatient,
    setCurrentPatient: (patient: CurrentPatient) => {},
    axiosPatient: defaultAxiosPatient,
    setAxiosPatient: (patient: AxiosPatient) => {},
    isChecked: false,
    setIsChecked: (approved: boolean) => {},
    openNewCenter: false,
    setOpenNewCenter: (openCenter: boolean) => {},
    openNewUser: false,
    setOpenNewUser: (openUser: boolean) => {},
    selectedCenterIdAdmin: 0,
    setSelectedCenterIdAdmin: (id: number) => {},
    selectedUserId: 0,
    setSelectedUserId: (id: number) => {},
    activeAdminTab: 0,
    setActiveAdminTab: (id: number) => {},
    newPatient: false,
    setNewPatient: (openUser: boolean) => {},
});

export default GlobalContext
